import { AppProvider } from '@ifixit/app/hooks';
import { createRoot } from 'react-dom/client';
import { store } from 'Shared/ifixit_store';
import { Provider } from 'react-redux';
import { withErrorInfo } from 'Shared/logging';
import { initializeReactQuery } from 'Shared/react-query-initialize';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { getGrowthbook } from 'Shared/features';
import { ViewLangIntlProvider } from 'Shared/use_intl';
import { ChakraDefaultProvider } from 'Shared/chakra-provider';
import { onDomReady } from 'Shared/utils';
import InstantSearchHero from './search_hero/instant_search_hero';
import { WPCampaignBanner } from 'Shared/Campaigns/campaign-banner-wp';
import { Box, Flex, VStack } from '@chakra-ui/react';

type HomePageHeroProps = {
   type: string;
   prompt: string;
   hasCampaigns: boolean;
   campaignEnabled: boolean;
   heroImagePath: string;
   sizes: {
      intermediate_width: number;
      small_width: number;
      space: number;
      default: number;
      small: number;
      intermediate: number;
   };
};

const HomePageHero = (props: HomePageHeroProps) => {
   // Only render the interactive content (search box)
   // The image and overlay are already in the placeholder and should not be replaced
   const fallbackSearchHero = (
      <Flex
         height={{
            base: props.sizes.default + 'px',
            md: props.sizes.small + 'px',
            lg: props.sizes.intermediate + 'px',
         }}
         flexDirection="column"
         justifyContent="center"
         alignItems="center"
         paddingTop={3}
         paddingBottom={{ base: 5, md: 3 }}
         position="relative"
         overflow="hidden"
         width="100%"
      >
         <VStack spacing={2} align="center" fontSize="16px" position="relative" zIndex={1}>
            <InstantSearchHero {...props} />
         </VStack>
      </Flex>
   );

   return (
      <Box position="relative">
         {props.campaignEnabled ? (
            <WPCampaignBanner {...props} fallbackComponent={fallbackSearchHero} />
         ) : (
            fallbackSearchHero
         )}
      </Box>
   );
};

// Custom initialization that preserves the placeholder image
// and only renders React content into the .hero-content container
// This prevents the flash when React replaces the placeholder image
onDomReady(() => {
   const selector = `.react-component[data-name="HomePageHero"]`;
   const placeholders = document.querySelectorAll(selector);

   Array.from(placeholders).forEach(placeholder => {
      const element = placeholder as HTMLElement;
      // Find the hero-content container inside the placeholder
      // The image is in .hero-image and should not be replaced
      const heroContent = element.querySelector('.hero-content');
      if (!heroContent) {
         // Fallback: if structure is different, skip (should not happen)
         console.warn('HomePageHero: .hero-content not found');
         return;
      }

      const props = {
         ...JSON.parse(element.dataset.props || '{}'),
      };

      withErrorInfo(async () => {
         const root = createRoot(heroContent);
         const growthbook = await getGrowthbook();
         root.render(
            initializeReactQuery(
               <ViewLangIntlProvider>
                  <GrowthBookProvider growthbook={growthbook}>
                     <Provider store={store}>
                        <AppProvider
                           ifixitOrigin={window.origin}
                           isLoggedIn={App.isLoggedIn}
                           priceTiers={App.priceTiers}
                           isOnMobileApp={App.isOnMobileApp}
                           storeLocale={App.userPreferredStoreLocale}
                        >
                           <ChakraDefaultProvider>
                              <HomePageHero {...props} />
                           </ChakraDefaultProvider>
                        </AppProvider>
                     </Provider>
                  </GrowthBookProvider>
               </ViewLangIntlProvider>
            )
         );
      }, ['Error occurred while rendering HomePageHero', props]);
   });
});
